import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/lib/AuthContext';
import { playStreak } from '@/utils/sounds';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserDataContext = createContext(null);

export const useUserData = () => {
  const ctx = useContext(UserDataContext);
  if (!ctx) throw new Error('useUserData must be used within UserDataProvider');
  return ctx;
};

export const UserDataProvider = ({ children }) => {
  const { user, session } = useAuth();

  // ─── User profile data from Supabase ─────────────────────────
  const [userData, setUserData] = useState(null);

  // ─── Streaks ─────────────────────────────────────────────────
  const [streakCount, setStreakCount] = useState(0);
  const [showStreakCelebration, setShowStreakCelebration] = useState(false);
  const prevStreakRef = useRef(0);

  // ─── Mastery ─────────────────────────────────────────────────
  const [masteryData, setMasteryData] = useState([]);
  const [masteryLoading, setMasteryLoading] = useState(false);

  // ─── Daily goals + pet mood ──────────────────────────────────
  const [dailyGoals, setDailyGoals] = useState({ listened: false, quizzed: false, asked: false });
  const [petMood, setPetMood] = useState('happy');

  // ─── Dedup ref ───────────────────────────────────────────────
  const hasFetchedInitialData = useRef(false);

  // ─── Auth helpers ────────────────────────────────────────────
  const getAuthHeaders = useCallback(() => {
    if (session?.access_token) return { Authorization: `Bearer ${session.access_token}` };
    return {};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.access_token]);

  // ─── Fetch functions ─────────────────────────────────────────
  const fetchStreak = useCallback(async () => {
    if (!user?.email) return;
    try {
      const res = await axios.get(`${API}/streak/${encodeURIComponent(user.email)}`);
      const newStreak = res.data.current_streak || 0;
      if (newStreak > 0 && newStreak > prevStreakRef.current) {
        setShowStreakCelebration(true);
        setTimeout(() => setShowStreakCelebration(false), 3000);
        playStreak();
      }
      setStreakCount(newStreak);
      localStorage.setItem('you_streak_count', String(newStreak));
      prevStreakRef.current = newStreak;
    } catch (err) { console.warn('UserData:', err?.message || err); }
  }, [user?.email]);

  const fetchMastery = useCallback(async () => {
    if (!user?.email) return;
    setMasteryLoading(true);
    try {
      const res = await axios.get(`${API}/mastery/${encodeURIComponent(user.email)}`);
      setMasteryData(res.data.topics || []);
    } catch (err) {
      console.error("fetchMastery error:", err);
    } finally {
      setMasteryLoading(false);
    }
  }, [user?.email]);

  const fetchDailyGoals = useCallback(async () => {
    if (!user?.email) return;
    try {
      const res = await axios.get(`${API}/daily-goals?user_email=${encodeURIComponent(user.email)}`);
      setDailyGoals({ listened: !!res.data.listened, quizzed: !!res.data.quizzed, asked: !!res.data.asked });
    } catch (err) { console.warn('UserData:', err?.message || err); }
  }, [user?.email]);

  const updateDailyGoal = useCallback(async (goalType) => {
    if (!user?.email) return;
    setDailyGoals(prev => ({ ...prev, [goalType]: true })); // optimistic
    try {
      await axios.post(`${API}/daily-goals/update`, { user_email: user.email, goal_type: goalType });
    } catch (err) { console.warn('UserData:', err?.message || err); }
  }, [user?.email]);

  const fetchPetMood = useCallback(async () => {
    if (!user?.email) return;
    try {
      const res = await axios.get(`${API}/pet?user_email=${encodeURIComponent(user.email)}`);
      if (res.data.mood) setPetMood(res.data.mood);
    } catch (err) { console.warn('UserData:', err?.message || err); }
  }, [user?.email]);

  const addPetXPRemote = useCallback(async (xpAmount) => {
    if (!user?.email) return;
    try {
      await axios.post(`${API}/pet/add-xp`, { user_email: user.email, xp_amount: xpAmount });
    } catch (err) { console.warn('UserData:', err?.message || err); }
  }, [user?.email]);

  // ─── Load all user data on login (deduplicated) ──────────────
  useEffect(() => {
    if (user?.email && !hasFetchedInitialData.current) {
      hasFetchedInitialData.current = true;
      fetchStreak();
      fetchMastery();
      fetchDailyGoals();
      fetchPetMood();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  // Reset fetch flag when user signs out
  useEffect(() => {
    if (!user) hasFetchedInitialData.current = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!user]);

  // ─── Context value ───────────────────────────────────────────
  const value = {
    // State
    userData,
    streakCount,
    showStreakCelebration,
    masteryData,
    masteryLoading,
    dailyGoals,
    petMood,

    // Setters (for components that need direct control)
    setUserData,
    setStreakCount,
    setShowStreakCelebration,
    setMasteryData,
    setMasteryLoading,
    setDailyGoals,
    setPetMood,

    // Actions
    getAuthHeaders,
    fetchStreak,
    fetchMastery,
    fetchDailyGoals,
    updateDailyGoal,
    fetchPetMood,
    addPetXPRemote,
  };

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
};
